import { Badge } from "@/components/ui/badge";
import { Sparkles, BookOpen, Radio, Search, Zap, CheckCircle2 } from "lucide-react";
import heroImage from "@/assets/hero-paedagogische-fachkraefte.jpg";
import { LEKTIONEN_KURZ } from "@/config/webseiteFakten";

interface MembershipHeroProps {
  onStartTrial?: () => void;
  isAlreadyMember?: boolean;
}

type HeroFeature = {
  icon: typeof BookOpen;
  text: string;
  description: string;
  live?: boolean;
};

const heroFeatures: HeroFeature[] = [
  { icon: BookOpen, text: "Bibliothek", description: `${LEKTIONEN_KURZ} Lektionen, sofort einsetzbar` },
  { icon: Radio, text: "Liederwerkstatt", description: "Monatliche Live-Sessions", live: true },
  { icon: Search, text: "Intelligente Suche", description: "Findet in Sekunden das Richtige" },
  { icon: Zap, text: "Miko KI", description: "Dein Musik-Assistent, 24/7" },
];

const MembershipHero = (_props: MembershipHeroProps) => {
  return (
    <section className="relative min-h-[75vh] flex items-center overflow-hidden">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src={heroImage}
          alt="Pädagogische Fachkräfte mit Kindern beim Musizieren"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/80 to-background/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
      </div>

      <div className="container mx-auto px-6 sm:px-8 lg:px-12 relative z-10 pt-32 sm:pt-36 lg:pt-40 pb-20">
        <div className="max-w-3xl space-y-8 animate-fade-in">
          {/* Badge – kräftiges Pill mit Schatten */}
          <div className="inline-flex items-center gap-2 rounded-full bg-background pl-1.5 pr-4 py-1.5 shadow-lg ring-1 ring-border/60">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gradient-to-br from-primary to-accent text-primary-foreground">
              <Sparkles className="w-3.5 h-3.5" />
            </span>
            <span className="text-sm font-semibold text-foreground">
              Drei Modelle – für jede Situation das richtige
            </span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
            Finde die passende{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
              MusiKonzept-Mitgliedschaft
            </span>{" "}
            – für dich oder für deine ganze Kita
          </h1>

          {/* Subheadline */}
          <p className="text-xl text-muted-foreground leading-relaxed max-w-2xl">
            Kostenlos reinschnuppern, komplette Mitgliedschaft für Einzelpersonen
            oder das Musik-Kita-Paket für eure ganze Einrichtung – wähle unten,
            was zu dir passt.
          </p>

          {/* Mini-Pricing Teaser */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground pt-2">
            <CheckCircle2 className="w-4 h-4 text-accent" />
            Jederzeit kündbar · Kostenlos starten möglich
          </div>

          {/* Quick features */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-8">
            {heroFeatures.map((feature, idx) => (
              <div
                key={idx}
                className={`group relative flex flex-col gap-3 p-5 rounded-2xl bg-card/90 backdrop-blur-sm border transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                  feature.live
                    ? "border-accent/40 hover:border-accent/70 ring-1 ring-accent/20"
                    : "border-border/50 hover:border-primary/40"
                }`}
              >
                {feature.live && (
                  <span className="absolute -top-2 -right-2 inline-flex items-center gap-1 rounded-full bg-accent text-accent-foreground text-[10px] font-bold px-2 py-0.5 shadow-md tracking-wider">
                    <span className="relative flex w-1.5 h-1.5">
                      <span className="absolute inline-flex w-full h-full rounded-full bg-accent-foreground/70 animate-ping" />
                      <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-accent-foreground" />
                    </span>
                    LIVE
                  </span>
                )}
                <div
                  className={`w-11 h-11 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110 ${
                    feature.live
                      ? "bg-gradient-to-br from-accent to-accent/70 text-accent-foreground"
                      : "bg-primary/10 text-primary"
                  }`}
                >
                  <feature.icon className="w-5 h-5" />
                </div>
                <div className="space-y-0.5">
                  <p className="font-semibold text-foreground text-sm leading-tight">
                    {feature.text}
                  </p>
                  <p className="text-xs text-muted-foreground leading-snug">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MembershipHero;
